<script lang="ts">
	import { Dropdown, Frame, Tooltip } from "flowbite-svelte";
	import { twMerge, type ClassNameValue } from "tailwind-merge";
	import Icon from "#lib/components/Icon.svelte";
	import TokenMenu from "#lib/components/TokenMenu.svelte";
	import { headingStyles } from "#lib/components/content-editors/extensions/Heading.js";
	import ToolbarButton from "#lib/components/content-editors/toolbar/ToolbarButton.svelte";
	import FileManagerDialog from "#lib/components/images/file-management/FileManagerDialog.svelte";
	import type { ImagePickerChangeEvent } from "#lib/components/images/types.js";
	import type { SubstitutionToken, TokenProvider } from "#lib/tokenProvider.js";
	import type { Editor } from "@tiptap/core";

	interface Props {
		editor: Editor;
		disabled?: boolean;
		class?: ClassNameValue;
		onClickLink?: (e: Event) => void;
		provider?: TokenProvider;
	}

	let { editor, disabled = $bindable(false), class: className, onClickLink, provider }: Props = $props();
	let openImagePicker = $state(false);
	let tokenMenuOpen = $state(false);

	async function onAddImage(e: CustomEvent<ImagePickerChangeEvent>) {
		editor.chain().focus().setImage({ src: e.detail.value }).run();
	}

	function handleClickLink(e: Event) {
		editor.commands.focus();
		onClickLink?.(e);
	}

	function handleTokenSelect(token: SubstitutionToken) {
		editor.chain().focus().insertToken(token).insertContent(" ").run();
		tokenMenuOpen = false;
	}
</script>

{#if openImagePicker}
	<FileManagerDialog bind:open={openImagePicker} on:change={onAddImage} />
{/if}
<Frame class={twMerge("flex gap-1 rounded-tl-lg rounded-tr-lg border-b-0 border-gray-300 p-1", className)} border>
	<ToolbarButton {disabled} data-action-name="text-style">
		<Icon iconSet="material-symbols" name="text-fields" />
	</ToolbarButton>
	<Dropdown placement="bottom-start">
		<li style={headingStyles[3]} class="mb-0! rounded p-2 hover:bg-gray-100 dark:hover:bg-gray-600"><button type="button" class="flex items-center gap-2" onclick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Icon name="check" class={twMerge("h-4 w-4", !editor.isActive("heading", { level: 3 }) && "invisible")} />Heading</button></li>
		<li style={headingStyles[5]} class="mb-0! rounded p-2 hover:bg-gray-100 dark:hover:bg-gray-600"><button type="button" class="flex items-center gap-2" onclick={() => editor.chain().focus().toggleHeading({ level: 5 }).run()}><Icon name="check" class={twMerge("h-4 w-4", !editor.isActive("heading", { level: 5 }) && "invisible")} />Subheading</button></li>
		<li class="rounded p-2 hover:bg-gray-100 dark:hover:bg-gray-600"><button type="button" class="flex items-center gap-2" onclick={() => editor.chain().focus().setParagraph().run()}><Icon name="check" class={twMerge("h-4 w-4", !editor.isActive("paragraph") && "invisible")} />Body</button></li>
	</Dropdown>
	<Tooltip type="auto">Style</Tooltip>
	<ToolbarButton {disabled} data-action-name="bold" checked={editor.isActive("bold")} on:click={() => editor.chain().focus().toggleBold().run()}>
		<span class="sr-only">Bold</span>
		<Icon iconSet="mdi" name="format-bold" />
	</ToolbarButton>
	<Tooltip type="auto">Bold</Tooltip>
	<ToolbarButton {disabled} data-action-name="italic" checked={editor.isActive("italic")} on:click={() => editor.chain().focus().toggleItalic().run()}>
		<span class="sr-only">Italic</span>
		<Icon iconSet="mdi" name="format-italic" />
	</ToolbarButton>
	<Tooltip type="auto">Italic</Tooltip>
	<ToolbarButton {disabled} data-action-name="underline" checked={editor.isActive("underline")} on:click={() => editor.chain().focus().toggleUnderline().run()}>
		<span class="sr-only">Underline</span>
		<Icon iconSet="mdi" name="format-underline" />
	</ToolbarButton>
	<Tooltip type="auto">Underline</Tooltip>
	<ToolbarButton {disabled} data-action-name="strikethrough" checked={editor.isActive("strike")} on:click={() => editor.chain().focus().toggleStrike().run()}>
		<span class="sr-only">Strike</span>
		<Icon iconSet="mdi" name="format-strikethrough" />
	</ToolbarButton>
	<Tooltip type="auto">Strike</Tooltip>
	<ToolbarButton {disabled} data-action-name="add-photo" onclick={() => (openImagePicker = true)}>
		<span class="sr-only">Image</span>
		<Icon name="photo" />
	</ToolbarButton>
	<Tooltip type="auto">Image</Tooltip>
	<ToolbarButton {disabled} data-action-name="add-hyperlink" checked={editor.isActive("link")} on:click={handleClickLink}>
		<span class="sr-only">Link</span>
		<Icon name="link" />
	</ToolbarButton>
	<Tooltip type="auto">Link</Tooltip>
	{#if provider}
		<ToolbarButton {disabled} data-action-name="data-property-selector">
			<Icon iconSet="mdi" name="code-braces" />
		</ToolbarButton>
		<TokenMenu bind:open={tokenMenuOpen} {provider} onselect={handleTokenSelect} />
		<Tooltip type="auto">Data properties</Tooltip>
	{/if}
</Frame>
